// src/menu/SAL/SN_SAL_SAO_REG_001/UI_Layout_V2.tsx

import React from "react";
// 모든 컴포넌트 import (경로는 프로젝트 설정에 맞게 조정하세요)
import SnGrid from "../DesignerSnGrid";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "../DesignerSnResizable";
// JSON 설정 파일 import
import SearchPanel from "../DesignerSearchPanel";
import SnForm from "../DesignerSnForm";
import SnTab from "../DesignerSnTab";
import SnTabHeader from "../DesignerSnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

// 1. 컴포넌트 맵 정의
const ComponentMap: { [key: string]: any } = {
  SnGrid: SnGrid,
  SnButton: SnButton,
  SnResizeBar: SnResizeBar,
  SearchPanel: SearchPanel,
  SnForm: SnForm,
  SnTab: SnTab,
  SnTabHeader: SnTabHeader,
  SnFileUploadBox: SnFileUploadBox,
  div: "div", // 일반 div 태그 지원
};

// 2. Props 및 Config 타입 정의 (간소화)
interface DynamicProps {
  [key: string]: any;
}

interface TemplateEtcUiProps {
  dynamicProps: DynamicProps;
  layoutConfig: {
    layout: UIConfig[];
  };
}
export interface UIConfig {
  type: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  props?: { [key: string]: any };
  header?: UIConfig[];
  body?: UIConfig[];
  children?: UIConfig[];
}

/**
 * JSON 설정에 따라 UI를 동적으로 렌더링하는 재귀 함수
 */
const renderComponent = (
  config: UIConfig,
  dynamicProps: DynamicProps
): React.ReactNode => {
  const Component = ComponentMap[config.type];
  if (!Component) {
    console.error(`Unknown component type: ${config.type}`);
    return null;
  }

  // Props 조합 (JSON 정적 Props + 동적 Props/Handlers)
  let componentProps: { [key: string]: any } = {
    ...config.props,
    className: config.className,
    style: config.style,
  };

  // "_key"를 사용하여 동적 Props 주입
  for (const key in config.props) {
    if (key.endsWith("_key")) {
      const dynamicKey = config.props[key];
      const propName = key.substring(0, key.length - 4);
      componentProps[propName] = dynamicProps[dynamicKey];
    }
  }

  // Ref 처리 (JSON에서 ref 키를 찾아 Ref 객체 주입)
  if (config.props?.ref) {
    componentProps.ref = dynamicProps[config.props.ref];
  }

  // Events 객체 처리
  if (config.props?.events_key) {
    componentProps.events = dynamicProps[config.props.events_key];
  }

  // Header/Children 요소 처리 (재귀 호출)
  let children: React.ReactNode[] = [];
  if (config.children) {
    children = config.children.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }
  if (Array.isArray(config.header)) {
    componentProps.header = config.header.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }

  if (Array.isArray(config.body)) {
    componentProps.body = config.body.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }

  return <Component {...componentProps}>{children}</Component>;
};

interface RenderItemProps {
  config: UIConfig;
  dynamicProps: DynamicProps;
}

const RenderItem: React.FC<RenderItemProps> = React.memo(
  ({ config, dynamicProps }) => {
    const Component = ComponentMap[config.type];

    if (!Component) return null;

    // ✅ props 생성
    const mergedProps: any = {
      ...config.props,
      className: config.className,
      style: config.style,
    };

    /***************************************
     * 1. Ref 자동 연결 (string 절대 금지)
     ***************************************/
    if (config.props?.ref_key) {
      const key = config.props.ref_key;

      if (config.type === "SnGrid" && dynamicProps.gridRefs) {
        if (!dynamicProps.gridRefs.current[key]) {
          dynamicProps.gridRefs.current[key] = React.createRef();
        }
        mergedProps.ref = dynamicProps.gridRefs.current[key];
      }

      if (config.type === "SnForm" && dynamicProps.formRefs) {
        if (!dynamicProps.formRefs.current[key]) {
          dynamicProps.formRefs.current[key] = React.createRef();
        }
        mergedProps.ref = dynamicProps.formRefs.current[key];
      }

      if (config.type === "SnForm" && config.props?.gridref_key) {
        const gridKey = config.props.gridref_key;
        const gridRef = dynamicProps.gridRefs?.current?.[gridKey];

        if (gridRef) {
          mergedProps.gridref = gridRef;
        }
      }

      if (key === "searchRef" && dynamicProps.searchRef) {
        mergedProps.ref = dynamicProps.searchRef;
      }
    }

    if (typeof mergedProps.ref === "string") {
      delete mergedProps.ref;
    }

    if (config.type === "SearchPanel" && config.props?.search_key) {
      const searchKey = config.props.search_key;
      const searchData = dynamicProps[searchKey];
      if (searchData) {
        mergedProps.data = searchData.data;
        mergedProps.controls = searchData.controls;
        mergedProps.onValueChanged = searchData.onValueChanged;
      }
    }

    /***************************************
     * 2. Grid 데이터 바인딩
     ***************************************/
    if (
      (config.type === "SnGrid" || config.type === "SnForm") &&
      config.props?.etcProps
    ) {
      Object.assign(mergedProps, config.props.etcProps);
    }

    // ✅ 여기 바로 아래에 추가
    if (mergedProps.header && typeof mergedProps.header === "object") {
      mergedProps.header = (
        <RenderItem config={mergedProps.header} dynamicProps={dynamicProps} />
      );
    }
    if (config.type === "SnGrid" && config.props?.grid_key) {
      const gridKey = config.props.grid_key;
      const gridData = dynamicProps.grids?.[gridKey];

      if (gridData) {
        mergedProps.columnConfig = gridData.columns;
        mergedProps.rowData = gridData.dataSource;
        mergedProps.events = gridData.events;
        if (gridData.headerInfo) {
          mergedProps.headerInfo = gridData.headerInfo;
        }
        if (gridData.initSetting) {
          mergedProps.initSetting = gridData.initSetting;
        }
      }
    }

    if (config.type === "SnForm" && config.props?.form_key) {
      const formKey = config.props.form_key;
      const formData = dynamicProps.forms?.[formKey];

      if (formData) {
        mergedProps.controls = formData.controls;
        mergedProps.data = formData.data;
        mergedProps.events = formData.events;
        if (formData.headerInfo) {
          mergedProps.headerInfo = formData.headerInfo;
        }
        if (formData.initSetting) {
          mergedProps.initSetting = formData.initSetting;
        }
      }
    }

    if (config.type === "SnTabHeader" && config.props?.header_key) {
      const headerKey = config.props.header_key;
      const headerData = dynamicProps.headers?.[headerKey];

      if (headerData) {
        mergedProps.onBtnAddClick = headerData.onBtnAddClick;
        mergedProps.onBtnDeleteClick = headerData.onBtnDeleteClick;
        mergedProps.headerInfo = headerData.headerInfo;
        mergedProps.onHeaderValueChanged = headerData.onHeaderValueChanged;
        mergedProps.onBtnEtcClick = headerData.onBtnEtcClick;
      }
    }

    /***************************************
     * 3. 이벤트 자동 매핑
     ***************************************/
    if (config.props?.onClick_key) {
      mergedProps.onClick = dynamicProps[config.props.onClick_key];
    }

    if (config.props?.events_key) {
      mergedProps.events = dynamicProps[config.props.events_key];
    }

    const children = config.children?.map((child, index) => {
      return (
        <RenderItem
          key={child.id ?? index}
          config={child}
          dynamicProps={dynamicProps}
        />
      );
    });

    if (config.type === "SnTab") {
      if (Array.isArray(config.body)) {
        mergedProps.body = config.body.map((child: UIConfig) => (
          <RenderItem
            key={child.id}
            config={child}
            dynamicProps={dynamicProps}
          />
        ));
      }

      if (Array.isArray(config.header)) {
        mergedProps.header = config.header.map((child: UIConfig) => (
          <RenderItem
            key={child.id}
            config={child}
            dynamicProps={dynamicProps}
          />
        ));
      }

      return <Component {...mergedProps} />;
    }

    return <Component {...mergedProps}>{children}</Component>;
  }
);

const TemplateEtcUi = React.memo(
  ({ dynamicProps, layoutConfig }: TemplateEtcUiProps) => {
    return (
      <div className="h-full flex flex-col">
        {layoutConfig.layout.map((config, index) => {
          return (
            <RenderItem
              key={config.id ?? index}
              config={config}
              dynamicProps={dynamicProps}
            />
          );
        })}
      </div>
    );
  }
);

export default TemplateEtcUi;
